import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
  IconButton,
} from '@mui/material';
import { format } from 'date-fns';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  CalendarEvent,
  CreateCalendarEventDto,
  EventCategory,
  ReminderType,
  RecurringPattern,
  RecurringPeriod,
  ReminderUnit,
} from '@/types/calendar';

import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import { useDeleteCalendarEvent } from '@/api/endpoints/calendar/useDeleteCalendarEvent';
import { useUpdateCalendarEvent } from '@/api/endpoints/calendar/useUpdateCalendarEvent';
import { Building01Icon } from '@/assets/icons/Building01Icon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { XCloseIcon } from '@/assets/icons/XCloseIcon';

import FormSelect from '@/components/form/FormSelect';
import FormSwitch from '@/components/form/FormSwitch';
import FormTextField from '@/components/form/FormTextField';
import ModalWithForm from '@/components/modals/ModalWithForm';

// Extended DTO to include recurring_end_date field needed for recurring events
interface ExtendedCalendarEventDto extends CreateCalendarEventDto {
  recurring_end_date?: string;
  deletedReminderIds?: Array<number | string>;
}

interface Props {
  event: CalendarEvent;
  onClose: () => void;
  onEventDeleted?: () => void;
  onEventUpdated?: () => void;
}

interface ReminderIntervalForm {
  id?: number | string;
  value: number;
  unit: ReminderUnit;
  originalRemindAt?: string;
}

interface EditCalendarEventForm {
  id: number;
  title: string;
  description?: string;
  location?: string;
  start: string;
  end: string;
  all_day: boolean;
  property_location_id: number;
  building_id?: number;
  unit_id?: number;
  tenant_id?: number;

  category?: EventCategory;
  reminderType?: ReminderType;
  recurringPattern?: RecurringPattern | 'none';
  recurringEvery?: number;
  recurringPeriod?: RecurringPeriod;
  neverExpires?: boolean;
  reminderIntervals?: ReminderIntervalForm[];

  notifySelf?: boolean;
  notifyTenant?: boolean;
  notifyVendor?: boolean;
  vendorId?: string;
  vendorEmail?: string;
  vendorMessage?: string;
  emailNotification?: boolean;
  smsNotification?: boolean;
  pushNotification?: boolean;
  dashboardNotification?: boolean;

  // Store the original reminders array for reference
  reminders?: Array<{
    id: number | string;
    calendar_event_id: number;
    remind_at: string;
    type: string;
    unit: string;
    value: number;
    sent: boolean;
    created_at: string;
    updated_at: string;
  }>;

  isDateRange: boolean;
  multipleReminders: boolean;
}

const eventCategories = [
  { label: 'Lease', value: EventCategory.LEASE },
  { label: 'Payment', value: EventCategory.PAYMENT },
  { label: 'Maintenance', value: EventCategory.MAINTENANCE },
  { label: 'Inspection', value: EventCategory.INSPECTION },
  { label: 'Other', value: EventCategory.OTHER },
];


const recurringPatterns = [
  { label: 'Does not repeat', value: 'none' },
  { label: 'Daily', value: RecurringPattern.DAILY },
  { label: 'Weekly', value: RecurringPattern.WEEKLY },
  { label: 'Monthly', value: RecurringPattern.MONTHLY },
  { label: 'Yearly', value: RecurringPattern.YEARLY },
  { label: 'Custom...', value: RecurringPattern.CUSTOM },
];

const periodOptions = [
  { label: 'Days', value: RecurringPeriod.DAYS },
  { label: 'Weeks', value: RecurringPeriod.WEEKS },
  { label: 'Months', value: RecurringPeriod.MONTHS },
  { label: 'Years', value: RecurringPeriod.YEARS },
];

const reminderUnitOptions = [
  { label: 'Days before', value: ReminderUnit.DAYS_BEFORE },
  { label: 'Weeks before', value: ReminderUnit.WEEKS_BEFORE },
  { label: 'Months before', value: ReminderUnit.MONTHS_BEFORE },
];

const CalendarEventEditModal2: React.FC<Props> = ({
  event,
  onClose,
  onEventDeleted,
  onEventUpdated,
}) => {
  // Check if event spans multiple days
  const hasDateRange = new Date(event.start).toDateString() !== new Date(event.end).toDateString();
  const [isDateRange, setIsDateRange] = useState(hasDateRange);
  const [multipleReminders, setMultipleReminders] = useState((event.reminders?.length ?? 0) > 1);
  const initialReminderType = event.recurringSettings
    ? ReminderType.RECURRING
    : ReminderType.ONE_TIME;
  const initialNeverExpires = event.recurringSettings ? !event.recurringSettings.end_date : true;
  const initialIsRecurrenceEndActive =
    initialReminderType === ReminderType.RECURRING && !initialNeverExpires;

  let initialFormEnd = new Date(event.end).toISOString().split('T')[0];
  if (initialIsRecurrenceEndActive && event.recurringSettings?.end_date) {
    const [dateOnly] = new Date(event.recurringSettings.end_date).toISOString().split('T');
    initialFormEnd = dateOnly;
  }

  // Filter out reminders that are no longer in the event data
  const getActiveReminders = () => {
    if (!event.reminders) return [];

    return event.reminders.map(r => ({
      id: r.id,
      value: r.value,
      unit: r.unit,
      originalRemindAt: r.remind_at,
    }));
  };

  const form = useForm<EditCalendarEventForm>({
    defaultValues: {
      id: event.id,
      title: event.title,
      description: event.description || '',
      location: (event.metadata?.location as string) || '',
      start: new Date(event.start).toISOString().split('T')[0],
      end: initialFormEnd,
      all_day: event.all_day,
      property_location_id: event.propertyLocationId,
      building_id: event.buildingId,
      unit_id: event.unitId,
      tenant_id: event.tenantId,
      isDateRange: hasDateRange,
      multipleReminders: (event.reminders?.length ?? 0) > 1,
      category: event.metadata?.category || EventCategory.OTHER,
      reminderType: event.recurringSettings ? ReminderType.RECURRING : ReminderType.ONE_TIME,
      recurringPattern: event.recurringSettings?.pattern || 'none',
      recurringEvery: event.recurringSettings?.every || 1,
      recurringPeriod: event.recurringSettings?.period || RecurringPeriod.DAYS,
      neverExpires: event.recurringSettings ? !event.recurringSettings.end_date : true,
      reminderIntervals: getActiveReminders() || [{ value: 30, unit: ReminderUnit.DAYS_BEFORE }],
      reminders: event.reminders || [],
      notifySelf: event.metadata?.notifySelf || false,
      notifyTenant: event.metadata?.notifyTenant || false,
      notifyVendor: event.metadata?.notifyVendor || false,
      vendorId: event.metadata?.vendor?.id || '',
      vendorEmail: event.metadata?.vendor?.email || '',
      vendorMessage: event.metadata?.vendor?.message || '',
      emailNotification: event.metadata?.emailNotification || false,
      smsNotification: event.metadata?.smsNotification || false,
      pushNotification: event.metadata?.pushNotification || false,
      dashboardNotification: event.metadata?.dashboardNotification || false,
    },
  });

  const { control, watch, setValue, handleSubmit } = form;

  const updateCalendarEventMutation = useUpdateCalendarEvent({
    onSuccess: () => {
      onEventUpdated?.();
      onClose();
    },
  });

  const deleteCalendarEventMutation = useDeleteCalendarEvent({
    onSuccess: () => {
      onEventDeleted?.();
      onClose();
    },
  });

  const handleUpdateSubmit = async (data: EditCalendarEventForm) => {
    // Handle the 'none' pattern case
    const processedData = { ...data };
    if (processedData.recurringPattern === 'none') {
      processedData.reminderType = ReminderType.ONE_TIME;
      processedData.recurringPattern = undefined;
      processedData.recurringEvery = undefined;
      processedData.recurringPeriod = undefined;
      processedData.neverExpires = undefined;
    }
    
    // Extract location for metadata
    const { location } = processedData;
    // Fix the date conversion issue by explicitly handling the date parts
    const [startYear, startMonth, startDay] = processedData.start.split('-').map(Number);
    const startDate = new Date(Date.UTC(startYear, startMonth - 1, startDay));

    // Create a separate variable for recurring end date
    let recurringEndDate: Date | undefined;

    // Handle recurring end date separately if this is a recurring event with an end date
    if (processedData.reminderType === ReminderType.RECURRING && !processedData.neverExpires) {
      const [endYear, endMonth, endDay] = processedData.end.split('-').map(Number);
      recurringEndDate = new Date(Date.UTC(endYear, endMonth - 1, endDay));
    }

    // Normal event end date (used for non-recurring events or if neverExpires is true)
    let eventEndDate: Date;
    if (processedData.all_day) {
      eventEndDate = startDate;
    } else {
      const [endYear, endMonth, endDay] = processedData.end.split('-').map(Number);
      eventEndDate = new Date(Date.UTC(endYear, endMonth - 1, endDay));
    }

    // Find which reminders are missing from the current form compared to original event data
    const originalReminderIds = new Set();
    if (event.reminders) {
      event.reminders.forEach(reminder => {
        originalReminderIds.add(reminder.id);
      });
    }

    // Get IDs of reminders that are in the form
    const formReminderIds = new Set();
    if (processedData.reminderIntervals) {
      processedData.reminderIntervals.forEach(reminder => {
        if (reminder.id) {
          formReminderIds.add(reminder.id);
        }
      });
    }

    // Find reminders that were in the original data but not in the form
    const implicitlyDeletedReminderIds: Array<number | string> = [];
    originalReminderIds.forEach(id => {
      if (!formReminderIds.has(id)) {
        implicitlyDeletedReminderIds.push(id as number | string);
      }
    });

    // Combine with explicitly deleted reminders from this modal
    const allDeletedReminderIds = [...implicitlyDeletedReminderIds];

    // Map reminder intervals with IDs to prevent duplication
    const reminderIntervals = processedData.reminderIntervals
      ?.filter(
        r =>
          // Keep reminder if it has no ID (new reminder)
          // or if it has an ID that's not in deletedReminderIds
          !r.id || (typeof r.id === 'number' && !allDeletedReminderIds.includes(r.id))
      )
      .map(r => {
        // For existing reminders, include the ID
        if (r.id) {
          return {
            id: r.id,
            value: r.value,
            unit: r.unit,
          };
        }
        // For new reminders, just send value and unit
        return {
          value: r.value,
          unit: r.unit,
        };
      });

    // Build metadata object with all the metadata fields
    const metadata: Record<string, unknown> = {
      category: processedData.category,
      notifySelf: processedData.notifySelf,
      notifyTenant: processedData.notifyTenant,
      notifyVendor: processedData.notifyVendor,
      emailNotification: processedData.emailNotification,
      smsNotification: processedData.smsNotification,
      pushNotification: processedData.pushNotification,
      dashboardNotification: processedData.dashboardNotification,
    };

    // Add vendor info to metadata if notifying vendor
    if (processedData.notifyVendor && (processedData.vendorId || 
        processedData.vendorEmail || processedData.vendorMessage)) {
      metadata.vendor = {
        id: processedData.vendorId,
        email: processedData.vendorEmail,
        message: processedData.vendorMessage,
      };
    }

    // Add location to metadata if provided
    if (location) {
      metadata.location = location;
    }

    const updatedEvent: Partial<ExtendedCalendarEventDto> = {
      title: processedData.title,
      description: processedData.description,
      start: startDate.toISOString(),
      end: recurringEndDate ? recurringEndDate.toISOString() : eventEndDate.toISOString(),
      all_day: processedData.all_day,
      property_location_id: processedData.property_location_id,
      building_id: processedData.building_id,
      unit_id: processedData.unit_id,
      tenant_id: processedData.tenant_id,

      category: processedData.category,
      reminderType: processedData.reminderType,
      recurringPattern: processedData.recurringPattern,
      recurringEvery: processedData.recurringEvery,
      recurringPeriod: processedData.recurringPeriod,
      neverExpires: processedData.reminderType === ReminderType.RECURRING ?
        processedData.neverExpires : undefined,
      reminderIntervals,
      deletedReminderIds: allDeletedReminderIds,

      notifySelf: processedData.notifySelf,
      notifyTenant: processedData.notifyTenant,
      notifyVendor: processedData.notifyVendor,
      vendorId: processedData.vendorId,
      vendorEmail: processedData.vendorEmail,
      vendorMessage: processedData.vendorMessage,
      emailNotification: processedData.emailNotification,
      smsNotification: processedData.smsNotification,
      pushNotification: processedData.pushNotification,
      dashboardNotification: processedData.dashboardNotification,
    };

    // Only add metadata if it has properties
    if (Object.keys(metadata).length > 0) {
      updatedEvent.metadata = metadata;
    }

    // For recurring events with an end date, add it to the payload
    if (processedData.reminderType === ReminderType.RECURRING &&
      !processedData.neverExpires && recurringEndDate) {
      updatedEvent.recurring_end_date = recurringEndDate.toISOString();
    }

    if (processedData.reminderType !== ReminderType.RECURRING) {
      delete updatedEvent.recurringPattern;
      delete updatedEvent.recurringEvery;
      delete updatedEvent.recurringPeriod;
      delete updatedEvent.neverExpires;
      delete updatedEvent.recurring_end_date;
    }

    return updateCalendarEventMutation.mutateAsync({
      id: processedData.id,
      data: updatedEvent,
    });
  };

  const handleDeleteClick = async () => deleteCalendarEventMutation.mutateAsync(event.id);

  const addReminderInterval = () => {
    const currentIntervals = form.getValues('reminderIntervals') || [];
    form.setValue('reminderIntervals', [
      ...currentIntervals,
      { value: 30, unit: ReminderUnit.DAYS_BEFORE },
    ]);
  };

  const removeReminderInterval = (index: number) => {
    const currentIntervals = form.getValues('reminderIntervals') || [];
    form.setValue(
      'reminderIntervals',
      currentIntervals.filter((_, i) => i !== index)
    );
  };

  // Helper function to calculate and display the reminder date
  const calculateReminderDate = (value: number, unit: ReminderUnit) => {
    const startValue = form.watch('start');
    if (!startValue) return '';

    // Get the event start date
    const dt = new Date(startValue);
    const reminderDate = new Date(dt);

    // Calculate the reminder date based on the unit
    switch (unit) {
    case ReminderUnit.DAYS_BEFORE:
      reminderDate.setDate(reminderDate.getDate() - value);
      break;
    case ReminderUnit.WEEKS_BEFORE:
      reminderDate.setDate(reminderDate.getDate() - value * 7);
      break;
    case ReminderUnit.MONTHS_BEFORE:
      reminderDate.setMonth(reminderDate.getMonth() - value);
      break;
    default:
      break;
    }

    return format(reminderDate, 'MMM d, yyyy');
  };

  // const allDay = watch('all_day');
  const startDate = watch('start');
  const currentReminderType = watch('reminderType');
  const currentRecurringPattern = watch('recurringPattern');
  const currentNotifyVendor = watch('notifyVendor');
  const currentNeverExpires = watch('neverExpires');
  const isRecurrenceEndActive =
    currentReminderType === ReminderType.RECURRING && !currentNeverExpires;

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'isDateRange') {
        setIsDateRange(Boolean(value.isDateRange));
        if (!value.isDateRange && !isRecurrenceEndActive) {
          setValue('end', startDate);
        }
      } else if (name === 'multipleReminders') {
        setMultipleReminders(Boolean(value.multipleReminders));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue, startDate, isRecurrenceEndActive]);

  // Create styles based on Figma design
  const figmaStyles = {
    formSection: {
      p: 1.5,
      mb: 1.5,
      border: '1px solid',
      borderColor: 'grey.200',
      borderRadius: 1,
      boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
      backgroundColor: '#FFFFFF',
    },
    sectionTitle: {
      mb: 1,
      fontWeight: 500,
      fontSize: '14px',
      color: '#414651',
    },
    inputLabel: {
      fontWeight: 500,
      fontSize: '13px',
      color: '#414651',
      mb: 0.5,
    },
    notificationLabel: {
      '& .MuiFormControlLabel-label': {
        fontSize: '13px',
      },
    },
    inputField: {
      '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
        '& fieldset': {
          borderColor: '#D5D7DA',
        },
      },
      '& .MuiInputBase-input': {
        padding: '6px 10px',
        fontSize: '13px',
      },
      '& .MuiInputLabel-root': {
        fontSize: '13px',
        fontWeight: 500,
      },
    },
    formFieldContainer: {
      mb: 1,
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      mb: 0.5,
    },
    twoColumnLayout: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    },
    inlineGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 1,
    },
  };

  return (
    <ModalWithForm
      title="Edit Event"
      onClose={onClose}
      maxWidth="sm"
      form={form}
      onSubmit={handleSubmit(handleUpdateSubmit)}
      buttonColor="primary"
      buttonLabel="Save Changes"
      showCloseButton={false}
    >
      <MuiPageWrapper>
        <Box sx={figmaStyles.formSection}>
          <Typography variant="h6" sx={figmaStyles.sectionTitle}>
            Basic Information
          </Typography>

          <Box sx={figmaStyles.formFieldContainer}>
            <Typography sx={figmaStyles.inputLabel}>Title</Typography>
            <FormTextField
              name="title"
              control={control}
              textFieldProps={{
                fullWidth: true,
                InputProps: {
                  placeholder: 'e.g., HOA Fee Due, Lease Expiration',
                },
                sx: figmaStyles.inputField,
              }}
            />
          </Box>

          <Box sx={figmaStyles.inlineGroup}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={figmaStyles.inputLabel}>Category</Typography>
              <FormSelect
                name="category"
                control={control}
                options={eventCategories}
                muiSelectProps={{
                  sx: figmaStyles.inputField,
                }}
              />
            </Box>
          </Box>

          <Box sx={figmaStyles.formFieldContainer}>
            <Typography sx={figmaStyles.inputLabel}>Description</Typography>
            <FormTextField
              name="description"
              control={control}
              textFieldProps={{
                fullWidth: true,
                multiline: true,
                minRows: 2,
                maxRows: 6,
                InputProps: {
                  placeholder: 'Enter details about this event or reminder',
                },
                sx: figmaStyles.inputField,
              }}
            />
          </Box>
          <Box sx={figmaStyles.formFieldContainer}>
            <Typography sx={figmaStyles.inputLabel}>Event Location</Typography>
            <FormTextField
              name="location"
              control={control}
              textFieldProps={{
                fullWidth: true,
                InputProps: {
                  placeholder: 'e.g., Community Center, Pool Area, Meeting Room',
                },
                sx: figmaStyles.inputField,
              }}
            />
          </Box>
        </Box>

        <Box sx={figmaStyles.formSection}>
          <Typography variant="h6" sx={figmaStyles.sectionTitle}>
            Schedule
          </Typography>

          <Box sx={{ mb: 2 }}>
            <FormSwitch 
              name="isDateRange" 
              control={control} 
              controlLabel="Multi-day event" 
              defaultValue={isDateRange}
            />
          </Box>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <FormTextField
                name="start"
                label={isDateRange ? 'Start Date' : 'Event Date'}
                control={control}
                textFieldProps={{
                  fullWidth: true,
                  type: 'date',
                  InputLabelProps: { shrink: true },
                  sx: figmaStyles.inputField,
                  onChange: e => {
                    setValue('start', e.target.value);
                    if (!isDateRange && !isRecurrenceEndActive) {
                      setValue('end', e.target.value);
                    } else {
                      const currentEndDate = watch('end');
                      if (new Date(currentEndDate) < new Date(e.target.value)) {
                        setValue('end', e.target.value);
                      }
                    }
                  },
                }}
              />
            </Box>
            {isDateRange && (
              <Box sx={{ flex: 1 }}>
                <FormTextField
                  name="end"
                  label="End Date"
                  control={control}
                  textFieldProps={{
                    fullWidth: true,
                    type: 'date',
                    InputLabelProps: { shrink: true },
                    inputProps: {
                      min: startDate,
                    },
                    sx: figmaStyles.inputField,
                  }}
                />
              </Box>
            )}
          </Stack>

          <Box sx={figmaStyles.formFieldContainer}>
            <Typography sx={figmaStyles.inputLabel}>Repeat</Typography>
            <FormSelect
              name="recurringPattern"
              control={control}
              muiSelectProps={{
                sx: figmaStyles.inputField,
                onChange: (e) => {
                  const pattern = e.target.value;
                  if (pattern === 'none') {
                    setValue('reminderType', ReminderType.ONE_TIME);
                    setValue('neverExpires', true);
                  } else {
                    setValue('reminderType', ReminderType.RECURRING);
                    setValue('neverExpires', false);
                    // Set sensible defaults based on pattern
                    if (pattern === RecurringPattern.DAILY) {
                      setValue('recurringEvery', 1);
                      setValue('recurringPeriod', RecurringPeriod.DAYS);
                    } else if (pattern === RecurringPattern.WEEKLY) {
                      setValue('recurringEvery', 1);
                      setValue('recurringPeriod', RecurringPeriod.WEEKS);
                    } else if (pattern === RecurringPattern.MONTHLY) {
                      setValue('recurringEvery', 1);
                      setValue('recurringPeriod', RecurringPeriod.MONTHS);
                    } else if (pattern === RecurringPattern.YEARLY) {
                      setValue('recurringEvery', 1);
                      setValue('recurringPeriod', RecurringPeriod.YEARS);
                    }
                  }
                }
              }}
              options={recurringPatterns}
            />
          </Box>

          {currentRecurringPattern !== 'none' && currentRecurringPattern !== undefined && (
            <>
              {currentRecurringPattern === RecurringPattern.CUSTOM && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ ...figmaStyles.inputLabel, mb: 1 }}>
                    Custom Recurrence
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontSize: '13px', color: '#414651' }}>Every</Typography>
                    <FormTextField
                      name="recurringEvery"
                      control={control}
                      textFieldProps={{
                        type: 'number',
                        inputProps: { min: 1 },
                        sx: { ...figmaStyles.inputField, width: 80 },
                      }}
                    />
                    <FormSelect
                      name="recurringPeriod"
                      control={control}
                      muiSelectProps={{
                        sx: { ...figmaStyles.inputField, minWidth: 120 },
                      }}
                      options={periodOptions}
                    />
                  </Stack>
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography sx={{ ...figmaStyles.inputLabel, mb: 1 }}>Ends</Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={currentNeverExpires}
                        onChange={e => setValue('neverExpires', e.target.checked)}
                      />
                    }
                    label="Never"
                    sx={figmaStyles.notificationLabel}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!currentNeverExpires}
                        onChange={e => setValue('neverExpires', !e.target.checked)}
                      />
                    }
                    label="On date"
                    sx={figmaStyles.notificationLabel}
                  />
                  {!currentNeverExpires && (
                    <Box sx={{ ml: 4 }}>
                      <FormTextField
                        label="End Date"
                        name="end"
                        control={control}
                        textFieldProps={{
                          fullWidth: true,
                          type: 'date',
                          InputLabelProps: { shrink: true },
                          inputProps: {
                            min: startDate,
                          },
                          sx: { ...figmaStyles.inputField, maxWidth: 200 },
                        }}
                      />
                    </Box>
                  )}
                </Stack>
              </Box>

              {/* Show next occurrences preview */}
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontWeight: 500 }}>
                  Next occurrences:
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                  {(() => {
                    const baseDate = new Date(startDate);
                    const dates = [];
                    for (let i = 1; i <= 3; i++) {
                      let nextDate = new Date(baseDate);
                      const every = watch('recurringEvery') || 1;
                      const period = watch('recurringPeriod');
                      const isDailyPattern = currentRecurringPattern === RecurringPattern.DAILY ||
                        (currentRecurringPattern === RecurringPattern.CUSTOM &&
                          period === RecurringPeriod.DAYS);
                      const isWeeklyPattern = currentRecurringPattern === RecurringPattern.WEEKLY ||
                        (currentRecurringPattern === RecurringPattern.CUSTOM &&
                          period === RecurringPeriod.WEEKS);
                      const isMonthlyPattern =
                        currentRecurringPattern === RecurringPattern.MONTHLY ||
                        (currentRecurringPattern === RecurringPattern.CUSTOM &&
                          period === RecurringPeriod.MONTHS);
                      const isYearlyPattern = currentRecurringPattern === RecurringPattern.YEARLY ||
                        (currentRecurringPattern === RecurringPattern.CUSTOM &&
                          period === RecurringPeriod.YEARS);
                      
                      const multiplier = i * every;
                      
                      if (isDailyPattern) {
                        nextDate.setDate(nextDate.getDate() + multiplier);
                      } else if (isWeeklyPattern) {
                        nextDate.setDate(nextDate.getDate() + multiplier * 7);
                      } else if (isMonthlyPattern) {
                        // Handle month addition properly to avoid date drift
                        const baseYear = baseDate.getFullYear();
                        const baseMonth = baseDate.getMonth();
                        const baseDay = baseDate.getDate();
                        
                        // Calculate target month and year
                        const totalMonths = baseMonth + multiplier;
                        const targetYear = baseYear + Math.floor(totalMonths / 12);
                        const targetMonth = totalMonths % 12;
                        
                        // Create date with target year and month, and original day
                        nextDate = new Date(targetYear, targetMonth, baseDay);
                        
                        // Check if the day overflowed (e.g., Jan 31 -> Feb 31 doesn't exist)
                        if (nextDate.getDate() !== baseDay) {
                          // Set to last day of the target month
                          nextDate = new Date(targetYear, targetMonth + 1, 0);
                        }
                      } else if (isYearlyPattern) {
                        nextDate.setFullYear(nextDate.getFullYear() + multiplier);
                      }
                      dates.push(format(nextDate, 'MMM d, yyyy'));
                    }
                    return `${dates.join(', ')}...`;
                  })()}
                </Typography>
              </Box>
            </>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography sx={{ ...figmaStyles.inputLabel, mb: 1 }}>Reminders</Typography>
            <FormSwitch
              name="multipleReminders"
              control={control}
              controlLabel="Set multiple reminders"
              defaultValue={multipleReminders}
            />

            {multipleReminders ? (
              <Box sx={{ mt: 1 }}>
                {(form.watch('reminderIntervals') || []).map((interval, index) => (
                  <React.Fragment
                    key={
                      interval.id
                        ? `reminder-${interval.id}`
                        : `reminder-${interval.value}-${interval.unit}-${index}`
                    }
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <FormTextField
                        name={`reminderIntervals.${index}.value`}
                        control={control}
                        textFieldProps={{
                          type: 'number',
                          sx: { ...figmaStyles.inputField, width: 80 },
                          inputProps: { min: 1 },
                        }}
                      />
                      <FormSelect
                        control={control}
                        options={reminderUnitOptions}
                        muiSelectProps={{
                          sx: { ...figmaStyles.inputField, minWidth: 120 },
                        }}
                        {...form.register(`reminderIntervals.${index}.unit`)}
                      />
                      <IconButton
                        onClick={() => removeReminderInterval(index)}
                        sx={{ color: 'error.main', p: 0.5 }}
                      >
                        <XCloseIcon />
                      </IconButton>
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        ml: 1,
                        mb: 1,
                        color: 'text.secondary',
                        fontSize: '12px',
                      }}
                    >
                      Reminder date:{' '}
                      {calculateReminderDate(
                        form.watch(`reminderIntervals.${index}.value`) || 0,
                        form.watch(`reminderIntervals.${index}.unit`) || ReminderUnit.DAYS_BEFORE
                      )}
                    </Typography>
                  </React.Fragment>
                ))}
                <Button
                  startIcon={<PlusIcon />}
                  onClick={addReminderInterval}
                  variant="text"
                  color="primary"
                  size="small"
                  sx={{ mt: 0.5 }}
                >
                  Add reminder
                </Button>
              </Box>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <FormTextField
                  control={control}
                  textFieldProps={{
                    type: 'number',
                    sx: { ...figmaStyles.inputField, width: 80 },
                    inputProps: { min: 1 },
                    defaultValue: 1,
                  }}
                  {...form.register('reminderIntervals.0.value', {
                    setValueAs: value => (value === '' ? 1 : Number(value)),
                  })}
                />
                <FormSelect
                  control={control}
                  options={reminderUnitOptions}
                  muiSelectProps={{
                    sx: { ...figmaStyles.inputField, minWidth: 140 },
                  }}
                  {...form.register('reminderIntervals.0.unit')}
                />
              </Stack>
            )}
          </Box>
        </Box>

        <Box sx={figmaStyles.formSection}>
          <Typography variant="h6" sx={figmaStyles.sectionTitle}>
            Notifications
          </Typography>

          <Box sx={figmaStyles.inlineGroup}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={watch('notifySelf')}
                  onChange={e => setValue('notifySelf', e.target.checked)}
                />
              }
              label="Notify Myself"
              sx={figmaStyles.notificationLabel}
            />
          </Box>

          <Box sx={figmaStyles.inlineGroup}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={watch('emailNotification')}
                  onChange={e => setValue('emailNotification', e.target.checked)}
                />
              }
              label="Email"
              sx={figmaStyles.notificationLabel}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={watch('smsNotification')}
                  onChange={e => setValue('smsNotification', e.target.checked)}
                />
              }
              label="SMS"
              sx={figmaStyles.notificationLabel}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={watch('dashboardNotification')}
                  onChange={e => setValue('dashboardNotification', e.target.checked)}
                />
              }
              label="Dashboard"
              sx={figmaStyles.notificationLabel}
            />
          </Box>

          {currentNotifyVendor && (
            <Box sx={{ mt: 1 }}>
              <Box sx={figmaStyles.inlineGroup}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={figmaStyles.inputLabel}>Vendor ID</Typography>
                  <FormTextField
                    name="vendorId"
                    control={control}
                    textFieldProps={{
                      fullWidth: true,
                      InputProps: {
                        placeholder: 'Vendor ID',
                      },
                      sx: figmaStyles.inputField,
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={figmaStyles.inputLabel}>Vendor Email</Typography>
                  <FormTextField
                    name="vendorEmail"
                    control={control}
                    textFieldProps={{
                      fullWidth: true,
                      type: 'email',
                      InputProps: {
                        placeholder: 'Email',
                      },
                      sx: figmaStyles.inputField,
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ mt: 1 }}>
                <Typography sx={figmaStyles.inputLabel}>Message for Vendor</Typography>
                <FormTextField
                  name="vendorMessage"
                  control={control}
                  textFieldProps={{
                    fullWidth: true,
                    multiline: true,
                    rows: 2,
                    InputProps: {
                      placeholder: 'Message',
                    },
                    sx: figmaStyles.inputField,
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>

        {(event.buildingId || event.unitId || event.tenantId) && (
          <Box sx={figmaStyles.formSection}>
            <Typography variant="h6" sx={figmaStyles.sectionTitle}>
              Associated With
            </Typography>
            <Stack spacing={1}>
              {event.buildingId && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Building01Icon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="dataGridBody">
                    {event.building?.name || event.buildingId}
                  </Typography>
                </Stack>
              )}
              {event.unitId && (
                <Typography variant="dataGridBody">
                  Unit: {event.unit?.name || event.unitId}
                </Typography>
              )}
              {event.tenantId && (
                <Typography variant="dataGridBody">
                  Tenant: {event.tenant?.full_name || event.tenantId}
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        <Button
          variant="outlined"
          color="error"
          onClick={handleDeleteClick}
          sx={{ mt: 2, width: '100%' }}
        >
          Delete Event
        </Button>
      </MuiPageWrapper>
    </ModalWithForm>
  );
};

export default CalendarEventEditModal2;
